import { patientService } from 'src/patient/services/patient-service';
import { DetailedLedger } from 'src/shared/model/ledger';
import { getLedger } from 'src/patient/state/patient-thunk';
import { setLedgerData, updateLedgerWithDetails } from 'src/patient/state/patient-slice';

export class LedgerUtils {
    static async fetchAndValidateLedger(encounterId: number, dispatch: any) {
        const response = await dispatch(
            getLedger({
                encounterId,
                supressErrors: true,
            })
        );
        if (!response?.payload?.selectedPatientEncounter?.encounterLedger) {
            console.error("Invalid ledger response - encounterLedger missing:", response);
            return null;
        }
        return response.payload.selectedPatientEncounter.encounterLedger;
    }

    static async processLedgerEntry(entry: DetailedLedger, dispatch: any) {
        let detailedEntry = { ...entry };
        try {
            if ([1, 4, 5, 6].includes(entry.ledgerTypeId)) {
                const paymentDetails = await patientService.getPaymentDetails(entry.patientLedgerId);
                if (paymentDetails) {
                    detailedEntry.payment = paymentDetails;
                    detailedEntry.detailsLoaded = true;
                    dispatch(updateLedgerWithDetails([detailedEntry]));
                }
            } else if ([2].includes(entry.ledgerTypeId)) {
                const reversalDetails = await patientService.getReversalDetails(entry.patientLedgerId);
                if (reversalDetails) {
                    detailedEntry.reversal = reversalDetails;
                    detailedEntry.detailsLoaded = true;
                    dispatch(updateLedgerWithDetails([detailedEntry]));
                }
            } else if ([3, 9].includes(entry.ledgerTypeId)) {
                // ledgerTypeId 3: PAYMENT_DISPUTED (negative amount, original dispute)
                // ledgerTypeId 9: PAYMENT_DISPUTE_REVERSED (positive amount, dispute won)
                const disputeDetails = await patientService.getDisputeDetails(entry.patientLedgerId);
                if (disputeDetails) {
                    detailedEntry.dispute = disputeDetails;
                    detailedEntry.detailsLoaded = true;
                    dispatch(updateLedgerWithDetails([detailedEntry]));
                }
            } else if ([8].includes(entry.ledgerTypeId)) {
                const pfrDetails = await patientService.getPfrDetails(entry.patientLedgerId);
                if (pfrDetails) {
                    detailedEntry.pfrAdjustment = pfrDetails;
                    detailedEntry.detailsLoaded = true;
                    dispatch(updateLedgerWithDetails([detailedEntry]));
                }
            }
        } catch (err) {
            console.error(`Error fetching details for ledger ID ${entry.patientLedgerId}:`, err);
        }
        return detailedEntry;
    }

    static async processLedgerBatches(
        entries: DetailedLedger[], 
        dispatch: any, 
        batchSize: number = 25
    ) {

        for (let i = 0; i < entries.length; i += batchSize) {
            const batch = entries.slice(i, i + batchSize);
            const batchPromises = batch.map(entry => 
                LedgerUtils.processLedgerEntry(entry, dispatch)
            );
            await Promise.all(batchPromises);

            if (i + batchSize < entries.length) {
                await new Promise(resolve => setTimeout(resolve, 100));
            }
        }
    }

    static async fetchLedger({
        encounterId,
        dispatch,
    }: {
        encounterId: number,
        dispatch: any,
    }): Promise<any> {
        try {
            const fetchedLedgerData = await LedgerUtils.fetchAndValidateLedger(encounterId, dispatch);
            if (!fetchedLedgerData) {
                return null;
            }
    
            dispatch(setLedgerData(fetchedLedgerData));
    
            let entries = [];
            if (fetchedLedgerData.ledger && Array.isArray(fetchedLedgerData.ledger)) {
                entries = fetchedLedgerData.ledger;
            } else if (Array.isArray(fetchedLedgerData)) {
                entries = fetchedLedgerData;
            }
    
            if (entries.length > 0) {
                await LedgerUtils.processLedgerBatches(
                    entries,
                    dispatch,
                );
            }
    
            return fetchedLedgerData;
        } catch (error) {
            console.error("Error fetching ledger:", error);
            return null;
        }
    }

    static calculateDownpayments(ledgerEntries: DetailedLedger[], excludeAccountHolderPayments: boolean = false): DetailedLedger[] {
        if (!Array.isArray(ledgerEntries)) {
            return [];
        }

        // Get all payment IDs that have pending or lost disputes
        // First, find all payments with won/partial_won disputes (ledgerTypeId 9 means funds restored, ledgerTypeId 3 can also have won status)
        const wonDisputePaymentIds = new Set<number>();
        const partialWonAmounts = new Map<number, number>(); // paymentId -> restored amount
        
        ledgerEntries.forEach((entry: DetailedLedger) => {
            if (entry.dispute && entry.dispute.paymentId) {
                const status = entry.dispute.disputeStatus?.toLowerCase();
                // Check for won/partial_won in both ledgerTypeId 9 (reversal) and ledgerTypeId 3 (dispute)
                if ((entry.ledgerTypeId === 9 || entry.ledgerTypeId === 3) && (status === 'won' || status === 'partial_won')) {
                    wonDisputePaymentIds.add(entry.dispute.paymentId);
                    // For partial_won with ledgerTypeId 9, store the restored amount (the amount kept by merchant)
                    if (status === 'partial_won' && entry.ledgerTypeId === 9 && entry.dispute.disputeAmt) {
                        partialWonAmounts.set(entry.dispute.paymentId, Math.abs(entry.dispute.disputeAmt));
                    }
                }
            }
        });
        
        // Then, find payments with pending or lost disputes (but exclude those that have won)
        const disputedPaymentIds = new Set<number>();
        ledgerEntries.forEach((entry: DetailedLedger) => {
            if (entry.dispute && entry.dispute.paymentId && (entry.ledgerTypeId === 3 || entry.ledgerTypeId === 9)) {
                const status = entry.dispute.disputeStatus?.toLowerCase();
                
                // If this payment has a won dispute entry (ledgerTypeId 9), don't exclude it
                if (wonDisputePaymentIds.has(entry.dispute.paymentId)) {
                    return;
                }
                
                // Exclude payments with pending, under_review, needs_response, or lost disputes
                if (status === 'pending' || status === 'under_review' || status === 'needs_response' || status === 'lost') {
                    disputedPaymentIds.add(entry.dispute.paymentId);
                }
            }
        });

        const downpayments = ledgerEntries.filter((entry: DetailedLedger) => {
            // Must be a downpayment (ledgerTypeId === 1 or payment.paymentTypeId === 1 or payment.paymentTypeId === 5)
            const isDownpayment = entry.ledgerTypeId === 1 || entry.payment?.paymentTypeId === 1 || entry.payment?.paymentTypeId === 5;
            if (!isDownpayment) return false;

            // If details aren't loaded yet, still include the entry (it will show loading state)
            if (!entry.detailsLoaded || !entry.payment) return true;

            // Exclude failed payments
            const isNotFailed = entry.payment?.paymentStatus !== 'Failed';
            
            // Exclude refunded payments - check if payment is not refunded
            const isNotRefunded = !entry.payment?.isFullyRefunded;

            // Exclude payments that have pending or lost disputes
            const isNotDisputed = !entry.payment?.paymentId || !disputedPaymentIds.has(entry.payment.paymentId);

            // Exclude account holder payments based on parameter
            let isNotAccountHolderPayment = true;
            if (excludeAccountHolderPayments && entry.payment) {
                // Account holders should not see ACH payments that are not captured
                // Based on: ((item?.paymentMethodUsed?.ach && !item.isCaptured) && isAccountHolderUser) ? "" :
                const isAchPayment = entry.payment.paymentMethodTypeId === 2; // ACH payment method type ID
                const isNotCaptured = !entry.payment.isCaptured;
                
                if (isAchPayment && isNotCaptured) {
                    isNotAccountHolderPayment = false; // Exclude this payment for account holders
                } else {
                    isNotAccountHolderPayment = true; // Include this payment
                }
            }

            return isNotFailed && isNotRefunded && isNotDisputed && isNotAccountHolderPayment;
        });

        // Adjust amounts for partial_won disputes
        return downpayments.map(entry => {
            if (entry.payment?.paymentId && partialWonAmounts.has(entry.payment.paymentId)) {
                // Create a new entry with adjusted amount for partial_won
                return {
                    ...entry,
                    payment: {
                        ...entry.payment,
                        paymentAmt: partialWonAmounts.get(entry.payment.paymentId)!
                    },
                    ledgerAmt: partialWonAmounts.get(entry.payment.paymentId)!
                };
            }
            return entry;
        });
    }

    /**
     * Calculate the sum of downpayment amounts
     */
    static calculateDownpaymentsSum(downpayments: DetailedLedger[]): number {
        if (!Array.isArray(downpayments)) {
            return 0;
        }

        return downpayments.reduce((sum: number, entry: DetailedLedger) => {
            const paymentAmt = entry.payment?.paymentAmt || entry.ledgerAmt || 0;
            return sum + paymentAmt;
        }, 0);
    }

    static calculateDownpaymentsData(ledgerEntries: DetailedLedger[], excludeAccountHolderPayments: boolean = false): { downpayments: DetailedLedger[], downpaymentsSum: number } {
        const downpayments = LedgerUtils.calculateDownpayments(ledgerEntries, excludeAccountHolderPayments);
        const downpaymentsSum = LedgerUtils.calculateDownpaymentsSum(downpayments);
        
        return {
            downpayments,
            downpaymentsSum
        };
    }
}